
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';

const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [wallet, setWallet] = useState('');
  const [signature, setSignature] = useState('');
  const [nonce, setNonce] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(null);
  const [error, setError] = useState(null);
  const [storageSafe, setStorageSafe] = useState(true);

  useEffect(() => {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      setStorageSafe(true);
    } catch (err) {
      console.warn('🚫 localStorage access denied:', err);
      setStorageSafe(false);
    }
  }, []);

  const authenticateWallet = useCallback(async (address, providerInstance) => {
    if (isAuthenticating || !providerInstance || !address) return;

    if (!process.env.REACT_APP_BACKEND_URL) {
      const msg = 'REACT_APP_BACKEND_URL is not defined';
      setError(msg);
      throw new Error(msg);
    }

    setIsAuthenticating(true);
    setError(null);

    const normalized = address.toLowerCase();
    const sigKey = `tokengate_sig_${normalized}`;
    const sigTTLKey = `tokengate_sig_time_${normalized}`;
    const nonceKey = `tokengate_nonce_${normalized}`;

    let sig = null;
    let cachedNonce = null;
    let sigTimestamp = null;
    const now = Date.now();

    if (storageSafe) {
      try {
        sig = localStorage.getItem(sigKey);
        sigTimestamp = localStorage.getItem(sigTTLKey);
        cachedNonce = localStorage.getItem(nonceKey);
      } catch (e) {
        console.warn('⚠️ Failed to read from localStorage');
      }
    }

    const isExpired = !sigTimestamp || now - parseInt(sigTimestamp, 10) > 12 * 60 * 60 * 1000;

    try {
      const { data: nonceRes } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-nonce`,
        { address: normalized }
      );

      console.log('✅ Auth nonce received:', nonceRes.nonce);

      if (!sig || cachedNonce !== String(nonceRes.nonce) || isExpired) {
        const message = `Sign this message to authenticate with our store.\n\n✅ This will not create a transaction.\n🔒 We cannot access or authorize transactions on your behalf.\n\nNonce: ${nonceRes.nonce}`;
        sig = await providerInstance.getSigner().signMessage(message);

        if (storageSafe) {
          localStorage.setItem(sigKey, sig);
          localStorage.setItem(nonceKey, String(nonceRes.nonce));
          localStorage.setItem(sigTTLKey, String(now));
        }
      }

      setSignature(sig);
      setNonce(nonceRes.nonce);

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/authenticate-wallet`, {
        address: normalized,
        signature: sig,
      });

      setWallet(normalized);
    } catch (err) {
      const errorMsg = err.response
        ? `Backend error: ${err.response.status} ${err.response.data.message}`
        : `Authentication failed: ${err.message}`;
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsAuthenticating(false);
    }
  }, [isAuthenticating, storageSafe]);

  useEffect(() => {
    if (!storageSafe) return;

    try {
      const lastConnected = localStorage.getItem('wallet_connected');
      const storedAddress = localStorage.getItem('wallet_address');
      const sigKey = `tokengate_sig_${storedAddress}`;
      const nonceKey = `tokengate_nonce_${storedAddress}`;
      const discount = localStorage.getItem('wallet_discount_percentage');

      if (storedAddress) setWallet(storedAddress);
      if (sigKey) setSignature(localStorage.getItem(sigKey));
      if (nonceKey) setNonce(localStorage.getItem(nonceKey));
      if (discount) setDiscountPercentage(Number(discount));
    } catch (err) {
      console.warn('⚠️ Failed to rehydrate from localStorage:', err);
    }
  }, [storageSafe]);

  return (
    <WalletContext.Provider
      value={{
        wallet,
        setWallet,
        signature,
        setSignature,
        nonce,
        setNonce,
        authenticateWallet,
        discountPercentage,
        setDiscountPercentage,
        isAuthenticating,
        error,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => useContext(WalletContext);
